import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listVenuesForVerification, setVenueVerifiedBusiness } from "@/lib/reviews.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/admin/venue-verification")({
  head: () => ({ meta: [{ title: "Venue Verification — Admin" }] }),
  component: AdminVenueVerificationPage,
});

type Venue = {
  id: string;
  full_name: string | null;
  business_name: string | null;
  legal_trading_name: string | null;
  companies_house_number: string | null;
  business_email: string | null;
  is_verified_business: boolean;
  verified_business_at: string | null;
  created_at: string;
};

function AdminVenueVerificationPage() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const list = useServerFn(listVenuesForVerification);
  const setVerified = useServerFn(setVenueVerifiedBusiness);

  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [acting, setActing] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await list();
      setVenues((r.venues ?? []) as Venue[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load venues.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await load();
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return venues;
    return venues.filter((v) =>
      (v.full_name ?? "").toLowerCase().includes(q)
      || (v.business_name ?? "").toLowerCase().includes(q)
      || (v.business_email ?? "").toLowerCase().includes(q),
    );
  }, [venues, search]);

  const handleToggle = async (v: Venue) => {
    setActing(v.id);
    try {
      await setVerified({ data: { venue_id: v.id, verified: !v.is_verified_business } });
      toast.success(v.is_verified_business ? "Verification removed." : "Venue verified.");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update verification.");
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin</p>
        <h1 className="mt-3 font-display text-3xl">Venue Verification</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Award the "Verified business" badge to venues whose trading status, address, and identity have been confirmed.
        </p>

        <div className="mt-6">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, business, or email…"
            className="rounded-none"
          />
        </div>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">No venues found.</p>
        ) : (
          <div className="mt-8 space-y-3">
            {filtered.map((v) => (
              <article key={v.id} className="border border-border bg-card p-5 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display text-lg">{v.business_name || v.full_name || "Unnamed venue"}</p>
                  <p className="text-xs text-muted-foreground">{v.business_email ?? "—"}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Legal name:{" "}
                    {v.legal_trading_name ? (
                      <a
                        className="underline underline-offset-2"
                        href={`https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(
                          v.companies_house_number || v.legal_trading_name,
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {v.legal_trading_name}
                        {v.companies_house_number ? ` · ${v.companies_house_number}` : ""}
                      </a>
                    ) : (
                      "— not provided"
                    )}
                  </p>
                  {v.is_verified_business && v.verified_business_at && (
                    <p className="mt-1 text-[0.65rem] tracking-wide-luxury text-emerald-700">
                      Verified · {new Date(v.verified_business_at).toLocaleDateString("en-GB")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {v.is_verified_business ? (
                    <span className="border border-emerald-700 text-emerald-700 px-2 py-1 tracking-wide-luxury text-[0.65rem]">✓ Verified</span>
                  ) : (
                    <span className="border border-border text-muted-foreground px-2 py-1 tracking-wide-luxury text-[0.65rem]">Unverified</span>
                  )}
                  <Button
                    disabled={acting === v.id}
                    onClick={() => handleToggle(v)}
                    variant={v.is_verified_business ? "outline" : "default"}
                    className="rounded-none tracking-wide-luxury"
                  >
                    {v.is_verified_business ? "Remove verification" : "Verify business"}
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
